import React from "react";
import { Link } from "react-router-dom";
import { BadgeCheck, Server, Eye, SlidersHorizontal, FileCheck2, Database, ArrowRight } from "lucide-react";
import { Button } from "../ui/button";
import { SectionHeading, Reveal } from "../common/Section";

const CERTS = [
  { name: "ISO 9001", status: "Certified", note: "Quality management" },
  { name: "ISO 42001", status: "Certified", note: "AI management system" },
  { name: "SOC 2", status: "In progress", note: "Roadmap / readiness" },
];

const ASSURANCE = [
  { icon: Server, t: "Flexible deployment", d: "Hosted, private cloud or customer-controlled environments." },
  { icon: Eye, t: "Human oversight", d: "Escalation and final decisions stay with authorised people." },
  { icon: SlidersHorizontal, t: "Policy controls", d: "Versioned, organisation-defined policies applied in real time." },
  { icon: FileCheck2, t: "Reviewable audit records", d: "Linked, time-stamped evidence for every intervention." },
  { icon: Database, t: "Configurable data handling", d: "Control what is inspected, redacted and retained." },
  { icon: BadgeCheck, t: "Independent control layer", d: "Sits in front of any model — model and vendor agnostic." },
];

export default function TrustLayer() {
  return (
    <section className="border-t border-border py-16 lg:py-24 bg-muted/40">
      <div className="mx-auto max-w-7xl px-5 lg:px-8">
        <Reveal>
          <SectionHeading eyebrow="Enterprise assurance" title="Built for regulated enterprise." subtitle="Certified management systems, human oversight and evidence — designed to support alignment with your obligations. Your firm remains accountable." />
        </Reveal>

        <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-3">
          {CERTS.map((c) => (
            <div key={c.name} data-testid={`cert-${c.name.replace(/\s+/g, "-").toLowerCase()}`} className="flex items-center gap-3 rounded-xl border border-border bg-card px-5 py-4 shadow-sm">
              <BadgeCheck className={`h-6 w-6 shrink-0 ${c.status === "Certified" ? "text-signal-green" : "text-signal-amber"}`} />
              <div>
                <p className="text-sm font-semibold text-foreground">{c.name} <span className={`ml-1 text-xs font-normal ${c.status === "Certified" ? "text-signal-green" : "text-signal-amber"}`}>· {c.status}</span></p>
                <p className="text-xs text-muted-foreground">{c.note}</p>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {ASSURANCE.map((a) => {
            const Icon = a.icon;
            return (
              <div key={a.t} className="rounded-xl border border-border bg-card p-5 shadow-sm hover:shadow-md transition-shadow">
                <span className="inline-flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 text-primary"><Icon className="h-5 w-5" /></span>
                <p className="mt-3 text-sm font-semibold text-foreground">{a.t}</p>
                <p className="mt-1 text-sm text-muted-foreground">{a.d}</p>
              </div>
            );
          })}
        </div>

        <div className="mt-8">
          <Button asChild variant="outline" className="border-primary/30 bg-transparent hover:bg-primary/10 text-primary">
            <Link to="/trust-centre" data-testid="view-trust-centre">View Trust Centre <ArrowRight className="ml-1.5 h-4 w-4" /></Link>
          </Button>
        </div>
      </div>
    </section>
  );
}
